// How long fresh food usually keeps, counted from when it was added to the kitchen. Always shown as
// an estimate ("about 2 days left", "check it"), never as an expiry date: storage, packaging and
// when it was bought all change the real answer. Days are typical refrigerated times, broadly in
// line with USDA FoodKeeper guidance. Shelf-stable food (dry goods, cans, jars, spices, frozen) has
// no estimate.

import type { IngredientId } from './taxonomy';

const DAY = 86_400_000;

/** Typical days in the fridge once bought or opened. Missing = no estimate. */
const KEEPS: Partial<Record<IngredientId, number>> = {
  // Meat and fish
  chicken: 2, chicken_breast: 2, chicken_thighs: 2, chicken_drumsticks: 2, turkey_breast: 2,
  ground_turkey: 2, ground_beef: 2, ground_pork: 2, ground_lamb: 2, sausage: 2,
  steak: 4, pork_chops: 4, pork_shoulder: 4, beef_chuck: 4, lamb: 4,
  bacon: 7, ham: 5, chorizo: 7, prosciutto: 21,
  shrimp: 2, salmon: 2, white_fish: 2,
  tofu: 5, eggs: 28,
  // Dairy
  milk: 7, almond_milk: 7, buttermilk: 14, heavy_cream: 10, sour_cream: 14, yogurt: 14, greek_yogurt: 14,
  butter: 60, cheese: 21, cheddar: 28, parmesan: 60, gruyere: 28, provolone: 21, blue_cheese: 21,
  mozzarella: 7, feta: 7, ricotta: 5, paneer: 5, queso_fresco: 5, cream_cheese: 14, halloumi: 14,
  // Bread and fresh starches
  bread: 5, hamburger_buns: 5, hoagie_rolls: 4, pita_bread: 5, flour_tortillas: 14, corn_tortillas: 14,
  leftover_rice: 4, gnocchi: 21, dumpling_wrappers: 7,
  // Vegetables and herbs
  bean_sprouts: 2, asparagus: 3, corn: 3, peas: 3,
  spinach: 4, baby_spinach: 4, bok_choy: 4, fresh_basil: 4,
  kale: 5, lettuce: 5, mushrooms: 5, shiitake: 5, tomatoes: 5, zucchini: 5, eggplant: 5, broccoli: 5,
  green_beans: 5, cilantro: 5, fresh_dill: 5, fresh_mint: 5,
  cherry_tomatoes: 7, cucumber: 7, bell_pepper: 7, cauliflower: 7, scallions: 7, fresh_parsley: 7,
  chili_peppers: 10, leeks: 10,
  celery: 14, beets: 14, lemongrass: 14, tomatillos: 14,
  cabbage: 21, carrots: 21, ginger: 21, sweet_potatoes: 21,
  potatoes: 30, onion: 30, red_onion: 30, garlic: 60,
  // Fruit
  strawberries: 3, avocado: 4, pineapple: 4, bananas: 5, blueberries: 7, grapes: 7, plantains: 7,
  oranges: 21, lemon: 21, lime: 21, apples: 30,
  orange_juice: 7,
};

/** Days left at which an item counts as "use soon". */
export const SOON_DAYS = 2;

export type FreshState = 'fresh' | 'soon' | 'check';

export interface Freshness {
  /** Typical days it keeps. */
  keeps: number;
  /** Days since it was added. */
  age: number;
  /** Estimated days left; zero or less means it may be past its best. */
  left: number;
  state: FreshState;
}

export function keepsFor(id: IngredientId): number | null {
  return KEEPS[id] ?? null;
}

/** The estimate for one kitchen item, or null for food that keeps (no estimate). */
export function freshness(item: { ingredientId: IngredientId; addedAt: string }, now = Date.now()): Freshness | null {
  const keeps = keepsFor(item.ingredientId);
  const added = Date.parse(item.addedAt);
  if (keeps === null || Number.isNaN(added)) return null;
  const age = Math.max(0, Math.floor((now - added) / DAY));
  const left = keeps - age;
  return { keeps, age, left, state: left <= 0 ? 'check' : left <= SOON_DAYS ? 'soon' : 'fresh' };
}

/** Short label for a kitchen tile; null when there's nothing to say. */
export function freshLabel(f: Freshness | null): string | null {
  if (!f || f.state === 'fresh') return null;
  if (f.state === 'check') return 'Check it';
  return f.left === 1 ? 'About 1 day left' : `About ${f.left} days left`;
}

/**
 * What to use first: items the person marked "use soon", plus those the estimate says are near
 * their best (or past it, still worth checking before they're wasted).
 */
export function toUseFirst<T extends { ingredientId: IngredientId; addedAt: string; useSoon: boolean }>(items: T[], now = Date.now()): IngredientId[] {
  return items.filter((i) => i.useSoon || (freshness(i, now)?.state ?? 'fresh') !== 'fresh').map((i) => i.ingredientId);
}
